'use client';

import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { useEffect, useState } from 'react';

import { ZoobinIcon } from '@zoomit/icons';
import { getCookie, isSSR, Link } from '@zoomit/primitives';
import { isIOS, isMobile } from '@zoomit/web-kit';
import { Button } from '@zoomit/zoomit-ds';

export type ZoobinAppMarkets = {
  androidMarkets: { name: string; url: string }[];
  iosMarkets: { name: string; url: string }[];
};

const DISMISS_X = 120;
const DISMISS_UP = -80;
const STORAGE_KEY = 'ZoobinInstallPopupShown';
const COOKIE_NAME = 'app-info';

const ZoobinInstallPopup = () => {
  const controls = useAnimation();

  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isSSR) return;

    const isFromApp = getCookie(COOKIE_NAME);

    const alreadyShown = localStorage.getItem(STORAGE_KEY) === '1';
    if (isMobile && !isFromApp && !alreadyShown && !isIOS()) {
      setShow(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (show) {
      controls.start({
        y: 0,
        opacity: 1,
        transition: { type: 'spring', stiffness: 200, damping: 30 },
      });
    }
  }, [show, controls]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: -120, opacity: 0 }}
          animate={controls}
          exit={{ y: -120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 30 }}
          drag
          dragElastic={0.2}
          dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
          whileDrag={{ scale: 0.95, opacity: 0.9 }}
          onDragEnd={(_, info) => {
            const { offset } = info;
            const dismissLeft = offset.x < -DISMISS_X;
            const dismissRight = offset.x > DISMISS_X;
            const dismissUp = offset.y < DISMISS_UP;

            if (dismissLeft || dismissRight || dismissUp) {
              controls
                .start({
                  x: dismissLeft ? -500 : dismissRight ? 500 : 0,
                  y: dismissUp ? -200 : 0,
                  opacity: 0,
                  transition: { duration: 0.25 },
                })
                .then(handleClose);
            } else {
              controls.start({ x: 0, y: 0, opacity: 1 });
            }
          }}
          className="fixed inset-x-0 top-4 z-1100 flex justify-center"
        >
          <div className="bg-background2 dark:shadow-primary3 shadow-primary2 relative mx-4 flex min-h-16 w-full items-center gap-3 rounded-[8px] p-2 shadow-sm">
            <div>
              <ZoobinIcon className="border-borderPrimary h-12 w-12 rounded-[8px] border-1 p-2" />
            </div>
            <div className="flex flex-grow flex-col gap-1">
              <span className="text-subhead-bold">اپلیکیشن زوبین</span>
              <span className="text-subhead">انتخاب بهتر و خرید ارزان‌تر</span>
            </div>
            <Link href="/download">
              <Button
                size="fit"
                className="bg-primary3 rounded-full px-[18px] py-2"
                onClick={() => handleClose()}
              >
                <span className="text-subhead-bold text-white">نصب</span>
              </Button>
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ZoobinInstallPopup;
